import { useEffect, useRef, useState } from "react";
import { fetchCities, searchCities } from "../../../api/cities";
import type { CityDto } from "../../../types/city";
import { TextInput } from "../_components/AdminShell";
import { parseVisibilityListValue } from "../../../lib/formBuilder/visibilityOperators";

export function useCityNameById(enabled = true): Record<string, string> {
  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    fetchCities()
      .then((cities) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const city of cities) next[String(city.id)] = city.name;
        setNames(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return names;
}

function serializeCityIds(ids: string[], multiple: boolean): string {
  if (ids.length === 0) return "";
  return multiple ? ids.join("\n") : ids[0]!;
}

export function VisibilityCityValue({
  value,
  multiple,
  names: catalogNames,
  onChange,
}: {
  value: string;
  multiple: boolean;
  names: Record<string, string>;
  onChange: (value: string) => void;
}) {
  const selectedIds = parseVisibilityListValue(value);
  const selectedSet = new Set(selectedIds);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CityDto[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickedNames, setPickedNames] = useState<Record<string, string>>({});
  const requestIdRef = useRef(0);

  const names = { ...catalogNames, ...pickedNames };

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const cities = await searchCities(trimmed);
        if (requestIdRef.current !== requestId) return;
        setResults(
          cities.slice().sort((a, b) => a.name.localeCompare(b.name, "ru")),
        );
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setResults([]);
        setError(
          err instanceof Error ? err.message : "Не удалось найти города",
        );
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [query]);

  const selectCity = (city: CityDto) => {
    const id = String(city.id);
    setPickedNames((current) => ({ ...current, [id]: city.name }));
    const nextIds = multiple
      ? selectedSet.has(id)
        ? selectedIds
        : [...selectedIds, id]
      : [id];
    onChange(serializeCityIds(nextIds, multiple));
    setQuery("");
    setResults([]);
  };

  const removeCity = (id: string) => {
    onChange(
      serializeCityIds(
        selectedIds.filter((item) => item !== id),
        multiple,
      ),
    );
  };

  return (
    <div className="space-y-1.5">
      {selectedIds.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {selectedIds.map((id) => {
            const label = names[id] || `Город ${id}`;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 font-onest text-[11px] font-medium text-slate-700 ring-1 ring-slate-200"
              >
                {label}
                <button
                  type="button"
                  className="text-slate-400 hover:text-slate-700"
                  aria-label={`Убрать ${label}`}
                  onClick={() => removeCity(id)}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      ) : null}

      {multiple || selectedIds.length === 0 ? (
        <TextInput
          size="compact"
          value={query}
          placeholder="Начните вводить название города"
          onChange={setQuery}
        />
      ) : null}

      {isLoading ? (
        <p className="font-onest text-[11px] text-slate-500">Поиск…</p>
      ) : null}
      {error ? (
        <p className="font-onest text-[11px] text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      {!isLoading &&
      query.trim().length >= 2 &&
      results.length === 0 &&
      !error ? (
        <p className="font-onest text-[11px] text-slate-500">
          Ничего не найдено
        </p>
      ) : null}

      {results.length > 0 ? (
        <ul className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white">
          {results.map((city) => {
            const id = String(city.id);
            const active = selectedSet.has(id);
            return (
              <li key={city.id}>
                <button
                  type="button"
                  disabled={active}
                  onClick={() => selectCity(city)}
                  className={[
                    "flex w-full items-center px-2.5 py-1.5 text-left font-onest text-xs transition-colors",
                    active
                      ? "cursor-default bg-slate-50 text-slate-400"
                      : "text-slate-700 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <span className="truncate">{city.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
